import { datadogRum } from '@datadog/browser-rum'
import { PROD } from '@frame/index'
import * as types from '@script/types'
import * as Sentry from '@sentry/react'

import { BlendEventType } from './api/events/types'
import { events } from './events'
import { emitBlendEvent } from './events/blend'
import * as tracking from './tracking'

const COMMON_DISPLAY_MESSAGES = {
  UNABLE_TO_START_VERIFICATION: 'Unable to start your verification',
  UNABLE_TO_CONTINUE_VERIFICATION: 'Unable to continue your verification',
}

const COMMON_DISPLAY_REASONS = {
  DISPLAY_ERROR: 'Failed to display content',
  CRITICAL_ERROR: 'Failed to connect to Truework',
}

export const displayMessages: {
  [k in Exclude<types.ErrorCode, types.ErrorCode.Network | types.ErrorCode.Unauthorized>]: string
} = {
  [types.ErrorCode.Critical]: COMMON_DISPLAY_MESSAGES.UNABLE_TO_START_VERIFICATION,
  [types.ErrorCode.Error]: COMMON_DISPLAY_MESSAGES.UNABLE_TO_START_VERIFICATION,
  [types.ErrorCode.Display]: COMMON_DISPLAY_MESSAGES.UNABLE_TO_CONTINUE_VERIFICATION,
}

const networkDisplayMessages: {
  [k in types.NetworkErrorCode]: string
} = {
  [types.NetworkErrorCode.Session]: COMMON_DISPLAY_MESSAGES.UNABLE_TO_START_VERIFICATION,
  [types.NetworkErrorCode.Configure]: COMMON_DISPLAY_MESSAGES.UNABLE_TO_START_VERIFICATION,
  [types.NetworkErrorCode.Consent]: COMMON_DISPLAY_MESSAGES.UNABLE_TO_CONTINUE_VERIFICATION,
  [types.NetworkErrorCode.Employment]: COMMON_DISPLAY_MESSAGES.UNABLE_TO_CONTINUE_VERIFICATION,
}

export const codes = types.ErrorCode
export const networkCodes = types.NetworkErrorCode

type Extra = Record<string, string | number | boolean>

export type InternalError = {
  /**
   * A instance of Error, complete with stacktrace. For internal reporting
   * only.
   */
  error: Error
  code: types.ErrorCode
  networkErrorCode?: types.NetworkErrorCode
  /**
   * Shown on error screen and reported to parent window.
   */
  displayMessage: string
  /**
   * Shown on error screen
   */
  displayReason?: string
  /**
   * Everything else we want to send with an error
   */
  extra?: Extra
  /**
   * Error level to be emitted to tools like Sentry
   */
  level?: 'fatal' | 'error' | 'warning' | 'log' | 'info' | 'debug'
  endpoint?: string
}

type InternalErrorArgs = Pick<
  InternalError,
  'error' | 'code' | 'networkErrorCode' | 'extra' | 'displayReason' | 'level' | 'endpoint'
>

type CreateErrorArgs = InternalErrorArgs & {
  preventReport?: true
  setError: ((e: InternalError) => void) | null
} // undefined is the default [falsy] value

/**
 * Create an InternalError object. Does not report or set state.
 */
export function create({
  error,
  code = types.ErrorCode.Error,
  networkErrorCode = types.NetworkErrorCode.Session,
  extra = {},
  displayReason,
  level = 'error',
  preventReport,
  endpoint,
  setError,
}: CreateErrorArgs): InternalError {
  const isNetworkError = code === types.ErrorCode.Network
  const isUnauthorizedError = code === types.ErrorCode.Unauthorized
  const e = {
    error,
    code,
    displayMessage: getDisplayMessage(),
    extra,
    networkErrorCode,
    displayReason: getDisplayReason(),
    level,
    endpoint,
  }
  // Always call the setError callback if it's defined (even if preventReport is true)
  if (setError !== null) {
    setError(e)
  }

  // Default behavior is to emit the error.
  if (!preventReport) {
    report(e)
  }

  return e

  function getDisplayMessage() {
    if (isNetworkError || isUnauthorizedError) return networkDisplayMessages[networkErrorCode]

    return displayMessages[code]
  }

  function getDisplayReason() {
    if (displayReason) return displayReason

    if (code === types.ErrorCode.Display) return COMMON_DISPLAY_REASONS.DISPLAY_ERROR

    return COMMON_DISPLAY_REASONS.CRITICAL_ERROR
  }
}

export function parseExtra(input: unknown): Extra | undefined {
  if (typeof input !== 'object' || input === null) {
    return undefined
  }

  const parsedInfo: Extra = {}

  Object.entries(input).forEach(([key, value]) => {
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      parsedInfo[key] = value
    }
  })

  if (Object.keys(parsedInfo).length) {
    return parsedInfo
  } else {
    return undefined
  }
}

export function getErrorWithFallback(maybeError: unknown, fallbackError: Error) {
  if (maybeError instanceof Error) {
    return maybeError
  }

  return fallbackError
}

function removeComponentStack(extra?: Extra) {
  // Remove the `componentStack` attribute from `Extra` because it's often an unnecessary amount of detail.
  if (extra === undefined) {
    return extra
  }

  return Object.fromEntries(Object.entries(extra).filter(([k]) => k !== 'componentStack'))
}

export function getTraits(error: InternalError) {
  return {
    tags: {
      code: error.code,
      // Only pass networkErrorCode if it's defined
      ...(error.networkErrorCode != null ? { networkErrorCode: error.networkErrorCode } : {}),
    },
    extra: {
      ...removeComponentStack(error.extra),
      // Only pass displayReason if it's defined
      ...(error.displayReason != null ? { displayReason: error.displayReason } : {}),
      displayMessage: error.displayMessage,
    },
  }
}

/**
 * Report an InternalError to the parent window, as well as our internal
 * telemetry systems.
 */
function report(error: InternalError) {
  // report to parent
  events.emit('error', { code: error.code, message: error.displayMessage })

  emitBlendEvent({
    eventType: error.level === 'fatal' ? BlendEventType.MODAL_INITIALIZATION_ERROR : BlendEventType.INTERNAL_ERROR,
    eventMessage: error.displayMessage,
  })

  const traits = getTraits(error)

  if (PROD) {
    Sentry.captureException(error.error, { level: error.level, ...traits })
    datadogRum.addError(error.error)
  }

  // this is stubbed in dev already
  tracking.track(tracking.Events.InternalError, traits)
}

/*

Example usage:

    import * as errors from '@frame/src/lib/errors'

    const err = errors.create({
      error,
      type: errors.ErrorType.Network,
      displayMessage: `Network request failed`,
    })

    // do something with err value

    errors.report(err)
*/
